import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Container,
} from '@mui/material';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': { color: '#aab7c4' },
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    },
    invalid: { color: '#9e2146' },
  },
};

const Onboarding = ({ api, onComplete }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [form, setForm] = useState({
    billing_name: '',
    billing_email: '',
    billing_phone: '',
    billing_address: '',
    billing_country: '',
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/api/store')
      .then((res) => {
        const details = res.data.details || {};
        const store = res.data.store || {};
        const company = store.company || {};
        const account = store.account || {};

        setForm({
          billing_name: company.companyName || account.accountName || '',
          billing_email: account.accountEmail || details.billing_email || '',
          billing_phone: company.phone || '',
          billing_address: company.street || '',
          billing_country: company.countryCode || '',
        });
      })
      .catch((err) => {
        console.error('Failed to load store profile:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.billing_name.trim()) {
      setError('Full name is required.');
      return;
    }
    if (!stripe || !elements) {
      setError('Stripe is not ready yet. Please try again.');
      return;
    }

    setSubmitting(true);
    try {
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: form.billing_name,
          email: form.billing_email,
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setSubmitting(false);
        return;
      }

      await api.post('/api/onboarding/complete', {
        ...form,
        paymentMethodId: paymentMethod.id,
      });

      onComplete();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <img src="https://app.bundlefix.com/logo-bundlefix.png" alt="BundleFix" style={{ height: 48, marginBottom: 16 }} />
        <Typography variant="h5" fontWeight={700}>Start your free trial</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          7 days free — no charge today. Cancel anytime before your trial ends.
        </Typography>
      </Box>

      <Card>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Billing details</Typography>

            <TextField
              label="Full Name"
              fullWidth
              required
              value={form.billing_name}
              onChange={handleChange('billing_name')}
              margin="normal"
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={form.billing_email}
              onChange={handleChange('billing_email')}
              margin="normal"
            />
            <TextField
              label="Phone"
              fullWidth
              value={form.billing_phone}
              onChange={handleChange('billing_phone')}
              margin="normal"
            />
            <TextField
              label="Address"
              fullWidth
              value={form.billing_address}
              onChange={handleChange('billing_address')}
              margin="normal"
            />
            <TextField
              label="Country"
              fullWidth
              value={form.billing_country}
              onChange={handleChange('billing_country')}
              margin="normal"
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Payment method</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Your card will not be charged during the trial period.
            </Typography>

            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                mb: 2,
                '&:focus-within': { borderColor: 'primary.main' },
              }}
            >
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={submitting || !stripe}
              sx={{ mt: 1 }}
            >
              {submitting ? <CircularProgress size={22} color="inherit" /> : 'Start Free Trial'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Onboarding;
